/* eslint-disable camelcase */
import React, {
  useEffect, useState,
} from 'react';
import { cloneDeep } from 'lodash';
import { Box } from '@mui/material';
import { GlobalFilter } from '../shared/material_components';
import Modal from '../components/modals/modal';
import { QueryRecords } from '../tasks/fields/task-link-field';
import { RestApiForm } from '../tasks/types';
import { buildSelectableRecords, prepareSelectedRecords } from './record-picker-helper';
import RecordPickerItems from './record-picker-items';
import RecordPickerPagination from './record-picker-pagination';
import { LinkedRecord } from '../shared/material_components/linked-cell';
import { PickerItemHandler } from '../editor/fields/helpers/editor-fields-helper';

interface Props {
  open: boolean,
  onCloseClick: () => void,
  data: QueryRecords,
  onSubmitSelection: (taskIDs: any[]) => void,
  currentlyLinkedRecords: LinkedRecord[],
  callback: (filter: Record<string, any>[] | string) => void;
  form?: RestApiForm,
  isLoading: boolean,
  handlePageIncrement: () => void,
  pickerItemHandler: PickerItemHandler,
}

const RecordPicker: React.FC<Props> = (
  {
    open,
    onCloseClick,
    data,
    onSubmitSelection,
    currentlyLinkedRecords,
    callback,
    form,
    isLoading,
    handlePageIncrement,
    pickerItemHandler,
  },
) => {
  const [openTaskPicker, setOpenTaskPicker] = useState<boolean>(open);
  const [recordSelection, setRecordSelection] = useState<Record<string, any>>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [pickerRecords, setPickerRecords] = useState(cloneDeep(data.records || []));

  useEffect(() => {
    setPickerRecords(cloneDeep(data.records || []));
  }, []);

  useEffect(() => {
    setPickerRecords(cloneDeep(data.records || []));
  }, [data.records]);

  useEffect(() => {
    setOpenTaskPicker(open);
  }, [open]);

  useEffect(() => {
    callback(globalFilter);
  }, [globalFilter]);

  const handleClose = (): void => {
    onCloseClick();
    callback('');
    setGlobalFilter('');
    setPickerRecords(cloneDeep(data.records || []));
    setOpenTaskPicker(false);
  };

  useEffect(() => {
    const selectableRecords = buildSelectableRecords({
      pickerRecords,
      currentlyLinkedRecords: cloneDeep(currentlyLinkedRecords),
    });
    setRecordSelection(selectableRecords);
  }, [pickerRecords, currentlyLinkedRecords]);

  const handleRecordSelection = (recordId: string) => {
    const toggleCheck = !recordSelection[recordId].selected;
    const baseRecordSelection = cloneDeep(recordSelection);
    if (!pickerItemHandler.allowMultiple) {
      Object
        .keys(baseRecordSelection)
        .forEach((key: string) => { baseRecordSelection[key].selected = false; });
    }
    const newObject = {
      ...baseRecordSelection,
      [recordId]:
        {
          ...baseRecordSelection[recordId],
          selected: !!toggleCheck,
        },
    };
    setRecordSelection(newObject);
  };

  const handleSubmitSelection = () => {
    let selectionRecords;
    if (!pickerItemHandler.allowMultiple) {
      const newSelectedRecordId = Object.keys(recordSelection)
        .filter((key: string) => recordSelection[key].selected === true)[0];
      selectionRecords = [{
        recordId: newSelectedRecordId,
      }];
    } else {
      selectionRecords = prepareSelectedRecords({
        newRecordSelection: recordSelection,
        oldRecordSelection: currentlyLinkedRecords,
      });
    }
    callback('');
    onSubmitSelection(selectionRecords);
    onCloseClick();
    setPickerRecords(cloneDeep(data.records));
    setGlobalFilter('');
  };

  const handleGlobalFilter = (e: string) => {
    setGlobalFilter(e || '');
  };

  return (
    <>
      <Modal
        title={pickerItemHandler.recordLinkedTitle}
        onPositive={handleSubmitSelection}
        onNegative={handleClose}
        positive="Submit"
        negative="Cancel"
        maxWidth="sm"
        serverError={false}
        open={openTaskPicker}
      >
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <Box>
            <GlobalFilter
              form={form as RestApiForm}
              setGlobalFilter={handleGlobalFilter}
              resetFilter={false}
            />
          </Box>
          <RecordPickerItems
            pickerRecords={pickerRecords}
            recordSelection={recordSelection}
            handleRecordSelection={handleRecordSelection}
            allowMultiple={pickerItemHandler.allowMultiple}
            currentlyLinkedRecords={currentlyLinkedRecords}
            pickerItemView={pickerItemHandler.view}
            // @ts-ignore
            restForm={form}
          />
          <Box>
            <RecordPickerPagination
              data={data}
              isLoading={isLoading}
              pickerRecords={pickerRecords}
              handlePageIncrement={handlePageIncrement}
            />
          </Box>
        </Box>

      </Modal>
    </>
  );
};
export default RecordPicker;
